import PageMeta from "@/components/PageMeta";
import { LiveExecutionStrip } from "@/components/home/LiveExecutionStrip";
import { PersonalStrip } from "@/components/home/PersonalStrip";
import { MorningReportCard } from "@/components/home/MorningReportCard";
import { TrackRecordTicker } from "@/components/home/TrackRecordTicker";
import { TossMoversTable } from "@/components/home/TossMoversTable";
import { HomeZone } from "@/components/home/HomeZone";
import { HomeSubNav } from "@/components/home/HomeSubNav";
import { HomeToolsZone } from "@/components/home/HomeToolsZone";
import { CollapsibleWidget } from "@/components/home/CollapsibleWidget";
import { SiteFooter } from "@/components/SiteFooter";
import { EmailSignupWidget } from "@/components/EmailSignupWidget";




import { SectorChips } from "@/components/SectorChips";
import { ExchangeWidget } from "@/components/ExchangeWidget";
import { JustInHero } from "@/components/JustInHero";
import { PopularNowCard } from "@/components/PopularNowCard";
import { CategoryNewsSection } from "@/components/CategoryNewsSection";
import { MarketBriefingBar } from "@/components/MarketBriefingBar";
import { ContentFreshness } from "@/components/LastUpdatedIndicator";
import { PushNotificationButton } from "@/components/PushNotificationButton";

import { LiveCryptoPrices } from "@/components/LiveCryptoPrices";

import { TradingViewChart } from "@/components/TradingViewChart";
import { TradingViewMarketOverview } from "@/components/TradingViewMarketOverview";
import { FearGreedIndex } from "@/components/FearGreedIndex";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { DailyTokenPick } from "@/components/DailyTokenPick";
import { TossWidget } from "@/components/TossWidget";
import { UndervaluedGemsWidget } from "@/components/UndervaluedGemsWidget";
import { TossRunButton } from "@/components/TossRunButton";
import LowCapGemsWidget from "@/components/LowCapGemsWidget";
import QuarterlyRevenueLeaderboard from "@/components/QuarterlyRevenueLeaderboard";
import { SmartMoneyTracker } from "@/components/SmartMoneyTracker";
import { SwingTradeWidget } from "@/components/SwingTradeWidget";
import BitcoinMarketPhase from "@/components/BitcoinMarketPhase";
import { TokenUnlocksCalendar } from "@/components/TokenUnlocksCalendar";
import { SocialSignalAggregator } from "@/components/SocialSignalAggregator";
import { AnalystWatchX } from "@/components/AnalystWatchX";
import { AdvancedAnalytics } from "@/components/AdvancedAnalytics";
import { PortfolioExposure } from "@/components/PortfolioExposure";
import { MacroRegimePanel } from "@/components/MacroRegimePanel";
import { MarketAnalyticsFeature } from "@/components/MarketAnalyticsFeature";
import SevenYearCycleWidget from "@/components/cycle/SevenYearCycleWidget";



import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAffiliateTracking } from "@/hooks/useAffiliateTracking";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Settings, Newspaper, Activity, Clock, RefreshCw, Database, DollarSign, Rocket, Sparkles, ArrowRight, Brain, Zap, Target, BarChart3 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { useState, useEffect } from "react";
import { fetchLiveMarketData, scoreTokenOpportunities, getTopByCategory, ScoredCoinData } from "@/lib/marketData";
import { SimpleTokenCard } from "@/components/SimpleTokenCard";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";

interface Recommendations {
  bestOverall: ScoredCoinData[];
  defi: ScoredCoinData[];
  layer1: ScoredCoinData[];
  layer2: ScoredCoinData[];
  ai: ScoredCoinData[];
  gaming: ScoredCoinData[];
  meme: ScoredCoinData[];
  lastUpdated: string;
}

const Index = () => {
  const { trackAffiliateClick } = useAffiliateTracking();
  const { isAdmin, user } = useAuth();
  const location = useLocation();
  const isDailyPickDeepLink = location.hash === "#daily-pick";
  
  const [recommendations, setRecommendations] = useState<Recommendations | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRunningWorkflow, setIsRunningWorkflow] = useState(false);

  // Fetch latest article date for freshness indicator
  const { data: latestArticle } = useQuery({
    queryKey: ['latest-article-date'],
    queryFn: async () => {
      const { data } = await supabase
        .from('articles')
        .select('publish_date')
        .eq('status', 'published')
        .order('publish_date', { ascending: false })
        .limit(1)
        .single();
      return data;
    },
    refetchInterval: 5 * 60 * 1000,
  });

  useEffect(() => {
    loadRecommendations();
    
    // Real-time updates every 5 minutes
    const interval = setInterval(() => {
      loadRecommendations();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  async function loadRecommendations() {
    try {
      setLoading(true);
      
      // Fetch fresh market data
      const marketData = await fetchLiveMarketData();
      
      // Score all opportunities
      const scored = scoreTokenOpportunities(marketData);
      
      // Get top picks per category
      const recs: Recommendations = {
        bestOverall: scored.slice(0, 5),
        defi: getTopByCategory(scored, 'defi', 3),
        layer1: getTopByCategory(scored, 'layer1', 3),
        layer2: getTopByCategory(scored, 'layer2', 3),
        ai: getTopByCategory(scored, 'ai', 3),
        gaming: getTopByCategory(scored, 'gaming', 3),
        meme: getTopByCategory(scored, 'meme', 3),
        lastUpdated: new Date().toISOString()
      };
      
      setRecommendations(recs);
    } catch (error) {
      console.error('Failed to load recommendations:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await loadRecommendations();
    setIsRefreshing(false);
  };

  const handleRunWorkflow = async () => {
    setIsRunningWorkflow(true);
    toast.info("Starting data collection workflow...", {
      description: "This may take a few minutes to complete"
    });
    
    try {
      const { data, error } = await supabase.functions.invoke('run-complete-workflow');
      
      if (error) throw error;
      
      toast.success("Workflow completed successfully!", {
        description: `Collected ${data.summary.tokens_collected} tokens, found ${data.summary.opportunities_found} opportunities`
      });
      
      // Refresh recommendations after workflow completes
      await loadRecommendations();
    } catch (error: any) {
      console.error('Workflow failed:', error);
      toast.error("Workflow failed", {
        description: error.message || "Failed to run data collection workflow"
      });
    } finally {
      setIsRunningWorkflow(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Bare homepage keeps its own canonical identity. Only the
          /#daily-pick deep link hands the head over to the pick card. */}
      {!isDailyPickDeepLink && (
        <PageMeta
          title="GoToCoins | AI-Powered Crypto Analysis for Australian Traders"
          description="Live AUD crypto prices, a public pick track record, TOSS scores and an AI coin reviewer — built for Australian traders."
          path="/"
          type="website"
        />
      )}
      {/* Site Banner */}
      <div className="bg-primary text-primary-foreground py-1.5 text-center">
        <p className="text-sm font-medium tracking-wide">
          🚀 <span className="font-bold">GoToCoins</span> — AI-Powered Crypto Analysis for Australian Traders
        </p>
      </div>
      
      {/* Disclaimer banner is global — rendered once in App.tsx */}
      
      
      {/* Global navigation lives in <SiteNav /> (App.tsx) — no page-level header here. */}

      
      {/* Admin Controls */}
      {isAdmin && (
        <div className="container mx-auto px-4 py-4">
          <div className="flex gap-4 justify-end flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRunWorkflow}
              disabled={isRunningWorkflow}
            >
              <Database className="w-4 h-4 mr-2" />
              {isRunningWorkflow ? "Running..." : "Run Data Collection"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/admin/token-scoring'}
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Token Scoring
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/admin/protocol-revenue'}
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Protocol Revenue
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => window.location.href = '/admin/bootstrap'}
            >
              <Rocket className="w-4 h-4 mr-2" />
              Bootstrap Content
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/admin/scheduler'}
            >
              <Clock className="w-4 h-4 mr-2" />
              Scheduler Dashboard
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = '/blog-generator'}
            >
              <Newspaper className="w-4 h-4 mr-2" />
              Blog Generator
            </Button>
          </div>
        </div>
      )}

      <HomeSubNav />

      <main>
      {/* ================= ZONE 1 — RIGHT NOW ================= */}
      <HomeZone
        id="prices"
        eyebrow="Right now"
        title="Live market, today's pick, and the receipts"
        blurb="AUD prices, the pick we published today, and the running record of how past picks resolved."
      >
        <LiveExecutionStrip />
        <div className="container mx-auto px-4 pt-3 empty:hidden"><MorningReportCard /></div>
        <PersonalStrip />
        <section id="daily-pick" className="container mx-auto px-4 py-4">
          <DailyTokenPick />
          <div className="mt-3 text-center">
            <Link
              to="/daily-pick"
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              Read today's Daily Pick article →
            </Link>
          </div>
        </section>
        <TrackRecordTicker />
        <MacroRegimePanel />
        <MarketBriefingBar />
      </HomeZone>

      {/* ================= ZONE 2 — LATEST ================= */}
      <HomeZone
        id="news"
        eyebrow="Latest"
        title="News and analyst roundup"
        blurb="The latest crypto news for Australian traders, plus what the analysts we track are saying."
        tone="raised"
      >
        <JustInHero />
        <PopularNowCard />
        <CategoryNewsSection />
        <section className="container mx-auto px-4 py-4">
          <SectorChips />
        </section>
        <section className="container mx-auto px-4 py-4">
          <AnalystWatchX />
        </section>
      </HomeZone>

      {/* ================= ZONE 3 — DO SOMETHING ================= */}
      <HomeZone
        id="tools"
        eyebrow="Do something"
        title="Tools built for Aussie traders"
        blurb="Every tool on the site, once each, ordered by how often people actually use them."
      >
        <HomeToolsZone />
      </HomeZone>

      {/* ================= ZONE 4 — GO DEEPER ================= */}

      <HomeZone
        id="analytics"
        eyebrow="Go deeper"
        title="Deeper market analysis"
        blurb="Cycle context, trade setups, on-chain flows and the full TOSS opportunity table."
      >
        <section className="container mx-auto px-4 py-4">
          <BitcoinMarketPhase defaultCollapsed />
        </section>

        <section className="container mx-auto px-4 py-4">
          <CollapsibleWidget
            name="swing_trade_analyzer"
            title="Swing Trade Analyzer"
            description="Build a trade plan for any token — entry, support, resistance, take-profit and stop-loss based on your risk and time horizon."
            icon={<TrendingUp className="h-5 w-5 text-primary" />}
          >
            <SwingTradeWidget />
          </CollapsibleWidget>
        </section>

        <section className="container mx-auto px-4 py-4">
          <SmartMoneyTracker />
        </section>

        <TossMoversTable />

        <section className="container mx-auto px-4 py-4">
          <div className="flex flex-col items-center justify-center gap-4 p-6 rounded-xl bg-gradient-to-br from-card/80 via-card/60 to-card/80 backdrop-blur border-2 border-primary/30">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-2 bg-gradient-to-r from-primary via-purple-400 to-pink-400 bg-clip-text text-transparent">
                Find Your Next 10x Opportunity
              </h3>
              <p className="text-muted-foreground max-w-2xl">
                Run TOSS analysis to discover high-potential tokens in the $10M-$500M market cap sweet spot,
                available on Australian exchanges
              </p>
            </div>
            <TossRunButton />
          </div>
        </section>

        <section id="toss-widget" className="container mx-auto px-4 py-4">
          <TossWidget />
        </section>

        <section className="container mx-auto px-4 py-4">
          <UndervaluedGemsWidget />
        </section>

        <section className="container mx-auto px-4 py-4">
          <TokenUnlocksCalendar />
        </section>

        <section className="container mx-auto px-4 py-4">
          <PortfolioExposure />
        </section>

        <section className="container mx-auto px-4 py-4">
          <SevenYearCycleWidget />
        </section>

        <section className="container mx-auto px-4 py-4">
          <AdvancedAnalytics />
        </section>

        <section className="container mx-auto px-4 py-4">
          <SocialSignalAggregator />
        </section>

        <section className="container mx-auto px-4 py-4">
          <LowCapGemsWidget />
        </section>

        <section className="container mx-auto px-4 py-4">
          <QuarterlyRevenueLeaderboard />
        </section>

        <section className="container mx-auto px-4 py-4">
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-6">
              <h3 className="text-2xl font-bold text-foreground mb-6">Live Market Analysis</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-foreground mb-4">Advanced Chart</h4>
                  <TradingViewChart />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-foreground mb-4">Market Overview</h4>
                  <TradingViewMarketOverview />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="container mx-auto px-4 py-4">
          <FearGreedIndex />
        </section>

        <section className="container mx-auto px-4 py-4">
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Activity className="w-5 h-5 text-primary" />
                <h3 className="text-2xl font-bold text-foreground">Live Market Prices</h3>
                <Badge variant="secondary" className="gap-1">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                  LIVE
                </Badge>
              </div>
              <LiveCryptoPrices />
            </CardContent>
          </Card>
        </section>

        <section className="container mx-auto px-4 py-4">
          <Card className="bg-card/50 backdrop-blur border-border/50">
            <CardContent className="p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <h3 className="text-2xl font-bold text-foreground">Top Opportunities by Category</h3>
                </div>
                <div className="flex items-center gap-4">
                  {recommendations && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      Last updated: {formatDistanceToNow(new Date(recommendations.lastUpdated), { addSuffix: true })}
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={isRefreshing}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>

              {loading ? (
                <div className="space-y-6">
                  {[1, 2, 3].map(i => (
                    <div key={i}>
                      <Skeleton className="h-6 w-32 mb-3" />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Skeleton className="h-32" />
                        <Skeleton className="h-32" />
                        <Skeleton className="h-32" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : recommendations ? (
                <div className="space-y-8">
                  {([
                    ["DeFi", recommendations.defi],
                    ["Layer 1", recommendations.layer1],
                    ["Layer 2", recommendations.layer2],
                    ["AI", recommendations.ai],
                    ["Gaming", recommendations.gaming],
                    ["Meme", recommendations.meme],
                  ] as const).map(([label, tokens]) => (
                    <div key={label}>
                      <h4 className="text-lg font-semibold text-foreground mb-3">{label}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {tokens.map(token => (
                          <SimpleTokenCard
                            key={token.symbol}
                            name={token.name}
                            symbol={token.symbol}
                            price={token.price}
                            change24h={token.change_24h}
                            score={token.score}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">No opportunities found</p>
              )}
            </CardContent>
          </Card>
        </section>
      </HomeZone>

      {/* ================= ZONE 4 — NEWSLETTER (standalone) ================= */}
      <HomeZone
        id="newsletter"
        eyebrow="One email a day"
        title="Good Morning Crypto"
        blurb="The Aussie market brief at 6 AM AEST — top movers, new picks and what actually matters."
        tone="raised"
      >
        <section className="container mx-auto px-4 py-4">
          <div className="max-w-md mx-auto">
            <EmailSignupWidget />
          </div>
        </section>
      </HomeZone>

      <div id="buy-section" className="container mx-auto px-4 py-8">
        <ExchangeWidget />
      </div>


      <MarketAnalyticsFeature />
      </main>


      {/* PWA Install Prompt */}

      {/* Sticky Coin Reviewer CTA */}

      <SiteFooter />
    </div>

  );
};

export default Index;
